import { CloseOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import { Viewport } from 'components/ViewportProvider';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import translations from 'translations';
import { FulfillmentNote, FulfillmentNoteFeedbackTag, MutationSubmitFeedbackArgs } from 'types/schema';
import formatMoney from 'utils/formatMoney';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import SVGIcon from '../SVGIcon/SVGIcon';
import Check from './Check.svg';
import Mascot1 from './Mascot-1.svg';
import Mascot2 from './Mascot-2.svg';
import Mascot3 from './Mascot-3.svg';
import Mascot4 from './Mascot-4.svg';
import Mascot5 from './Mascot-5.svg';
import Mascot from './Mascot.svg';
import Symbol1 from './Symbol-1.svg';
import Symbol2 from './Symbol-2.svg';
import Symbol3 from './Symbol-3.svg';
import Symbol4 from './Symbol-4.svg';
import Symbol5 from './Symbol-5.svg';
import Symbol6 from './Symbol-6.svg';
import Symbol7 from './Symbol-7.svg';
import Symbol8 from './Symbol-8.svg';
import messages from './messages';

import { FormattedMessage } from 'react-intl';
import {
  CheckIcon,
  CheckIconMobile,
  Container,
  FeedbackSymbol,
  FeedbackSymbolMobile,
  FeedbackSymbolMobileWrapper,
  FeedbackText,
  FeedbackWrapper,
  LaterButton,
  NoteFeedBackWrapper,
  NoteLabel,
  OrderFlex,
  OrderFlexInfo,
  OrderId,
  OrderInfoText,
  OrderNote,
  OrderNoteIdWrapper,
  StyledModal,
  StyledRate,
  SubmitButton,
  Title,
} from './styles';

const { TextArea } = Input;

const MASCOT_MAPPING = {
  0: Mascot,
  1: Mascot1,
  2: Mascot2,
  3: Mascot3,
  4: Mascot4,
  5: Mascot5,
};

const RATING_TEXT = {
  1: messages.terrible,
  2: messages.poor,
  3: messages.fair,
  4: messages.good,
  5: messages.excellent,
};

const POSITIVE_FEEDBACK_SYMBOLS = [
  { icon: Symbol1, text: messages.asRequested, value: FulfillmentNoteFeedbackTag.RightRequirement },
  { icon: Symbol2, text: messages.carefullyPackaged, value: FulfillmentNoteFeedbackTag.CarefulPackaging },
  { icon: Symbol3, text: messages.FriendlyDeveliryStaff, value: FulfillmentNoteFeedbackTag.FriendlyShipper },
  { icon: Symbol4, text: messages.OnTimeDelivery, value: FulfillmentNoteFeedbackTag.OnTimeDelivery },
];

const NEGATIVE_FEEDBACK_SYMBOLS = [
  { icon: Symbol5, text: messages.wrongItemsDeliveried, value: FulfillmentNoteFeedbackTag.WrongDelivery },
  { icon: Symbol6, text: messages.poorPackaging, value: FulfillmentNoteFeedbackTag.PoorPackaging },
  { icon: Symbol7, text: messages.unfriendlyDeliveryStaff, value: FulfillmentNoteFeedbackTag.UnfriendlyShipper },
  { icon: Symbol8, text: messages.deliveryWasLate, value: FulfillmentNoteFeedbackTag.LateDelivery },
];

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  note: FulfillmentNote;
  onSubmitFeedback: (value: MutationSubmitFeedbackArgs['input']) => void;
}

interface FeedbackSymbolProps {
  icon: string;
  text: string;
  value: FulfillmentNoteFeedbackTag;
  selected?: boolean;
  onClick?: () => void;
  width: number;
}

const MascotImage = ({ rating }: { rating: number }) => {
  const mascot = MASCOT_MAPPING[rating as keyof typeof MASCOT_MAPPING] || Mascot;
  return <SVGIcon src={mascot} />;
};

const FeedbackSymbolItem = ({ icon, text, selected, onClick, width }: FeedbackSymbolProps) => (
  <>
    {width > 576 ? (
      <FeedbackSymbol key={text} selected={selected} onClick={onClick}>
        {selected && <CheckIcon src={Check} />}
        <SVGIcon src={icon} />
        <OrderInfoText width={width}>{text}</OrderInfoText>
      </FeedbackSymbol>
    ) : (
      <FeedbackSymbolMobileWrapper>
        <FeedbackSymbolMobile key={text} selected={selected} onClick={onClick}>
          {selected && <CheckIconMobile src={Check} />}
          <SVGIcon src={icon} />
        </FeedbackSymbolMobile>
        <OrderInfoText width={width}>{text}</OrderInfoText>
      </FeedbackSymbolMobileWrapper>
    )}
  </>
);

const OrderInfo = ({ note }: { note: FulfillmentNote }) => {
  const { fulfillmentNoteShortCode, deliveryEndDateTime, deliveryStartDateTime, itemCount, total } = note;

  return (
    <>
      <OrderNoteIdWrapper>
        <OrderNote>
          <FormattedMessage {...messages.deliveryNote} />:
        </OrderNote>
        <OrderId>{fulfillmentNoteShortCode}</OrderId>
      </OrderNoteIdWrapper>
      <OrderInfoText>
        {moment(deliveryStartDateTime).format('HH:mm')} - {moment(deliveryEndDateTime).format('HH:mm | DD/MM/YYYY')}
      </OrderInfoText>
      <OrderInfoText>
        {itemCount} <FormattedMessage {...messages.items} /> | {formatMoney(total)}đ
      </OrderInfoText>
    </>
  );
};

const RatingModal = ({ isOpen, onClose, note, onSubmitFeedback }: RatingModalProps) => {
  const [rating, setRating] = useState<number>(0);
  const [selectedFeedbacks, setSelectedFeedbacks] = useState<FulfillmentNoteFeedbackTag[]>([]);
  const { fulfillmentNoteShortCode, fulfillmentNoteId, feedback } = note;
  const [comment, setComment] = useState<string>('');

  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setSelectedFeedbacks([]);
    }
    return () => {
      setRating(0);
      setSelectedFeedbacks([]);
    };
  }, [isOpen]);

  const handleRatingChange = (value: number): void => {
    if (value > 0) {
      setRating(value);
      setSelectedFeedbacks([]);
    }
  };

  const toggleFeedbackSelection = (value: FulfillmentNoteFeedbackTag) => {
    setSelectedFeedbacks((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const renderFeedbackSymbols = (width: number) => {
    const symbols = rating <= 3 ? NEGATIVE_FEEDBACK_SYMBOLS : POSITIVE_FEEDBACK_SYMBOLS;
    return symbols.map(({ icon, text, value }) => (
      <FeedbackSymbolItem
        key={value}
        icon={icon}
        text={translations(text)}
        value={value}
        selected={selectedFeedbacks.includes(value)}
        onClick={() => toggleFeedbackSelection(value)}
        width={width}
      />
    ));
  };

  const handleLater = () => {
    // onSubmitFeedback({
    //   objectId: fulfillmentNoteId,
    //   objectType: 'FULFILLMENT_NOTE',
    //   dismissed: true,
    //   ...(feedback?.id ? { id: feedback.id } : {}),
    // });
    sendGA4Event(EventName.CLICK_LATER_RATING, {
      ffm_id: fulfillmentNoteId,
      ffm_shortcode: fulfillmentNoteShortCode,
    });
    onClose();
  };

  const handleSubmit = () => {
    onSubmitFeedback({
      objectId: fulfillmentNoteId,
      objectType: 'FULFILLMENT_NOTE',
      submitted: true,
      tags: selectedFeedbacks,
      rating: rating,
      comment: comment.trim(),
      ...(feedback?.id ? { id: feedback.id } : {}),
    });
    sendGA4Event(EventName.SUBMIT_RATING, {
      ffm_id: fulfillmentNoteId,
      ffm_shortcode: fulfillmentNoteShortCode,
      delivery_rating: rating,
      delivery_comment: comment.trim(),
      delivery_feedbacks: selectedFeedbacks.map((item) => ({ name: item })),
    });
    onClose();
  };

  return (
    <Viewport.Consumer>
      {({ width }) => (
        <StyledModal
          open={isOpen}
          onCancel={onClose}
          footer={null}
          closable={true}
          closeIcon={<CloseOutlined />}
          maskClosable={true}
        >
          <Container>
            <Title>
              {rating === 0 ? (
                <FormattedMessage {...messages.ratingTitle} />
              ) : (
                <FormattedMessage
                  {...messages.ratingTitle2}
                  values={{ fulfillmentNoteShortCode: fulfillmentNoteShortCode }}
                />
              )}
            </Title>

            <OrderFlexInfo>
              {rating === 0 ? (
                <OrderInfo note={note} />
              ) : (
                <OrderInfoText>
                  <FormattedMessage {...messages.ratingSubtext} />
                </OrderInfoText>
              )}
            </OrderFlexInfo>

            <MascotImage rating={rating} />

            <OrderFlex>
              <StyledRate rating={rating} onChange={handleRatingChange} value={rating} count={5} />
              <OrderInfoText>{translations(RATING_TEXT[rating as keyof typeof RATING_TEXT])}</OrderInfoText>
            </OrderFlex>

            {rating === 0 ? (
              <FeedbackText>
                <FormattedMessage {...messages.ratingText} />
              </FeedbackText>
            ) : (
              <FeedbackWrapper>{renderFeedbackSymbols(width)}</FeedbackWrapper>
            )}

            {rating > 0 && (
              <NoteFeedBackWrapper>
                <NoteLabel htmlFor="feedback">
                  <FormattedMessage {...messages.writeReview} />
                </NoteLabel>
                <TextArea
                  id="feedback"
                  rows={4}
                  placeholder={translations(messages.reviewPlaceHolder)}
                  maxLength={100}
                  onChange={(e) => setComment(e.target.value)}
                  style={{ resize: 'none' }}
                />
              </NoteFeedBackWrapper>
            )}

            {rating === 0 ? (
              <LaterButton type="primary" size="large" ghost onClick={handleLater}>
                <FormattedMessage {...messages.later} />
              </LaterButton>
            ) : (
              <SubmitButton size="large" onClick={handleSubmit}>
                <FormattedMessage {...messages.submit} />
              </SubmitButton>
            )}
          </Container>
        </StyledModal>
      )}
    </Viewport.Consumer>
  );
};

export default RatingModal;
